import { useMutation } from '@tanstack/react-query'

import { Button } from '@/components/ui/Button'
import { Card, CardBody, CardHeader } from '@/components/ui/Card'

import { evaluateRecommendations } from './api'

const pct = (value: number) => `${(value * 100).toFixed(1)}%`

/**
 * How often the cross-sell panel is right, measured on orders the model never saw.
 *
 * Measured on demand rather than on page load: it retrains up to a cut-off, which takes a
 * few seconds, and the figure only moves when the order history does.
 */
export function AccuracyCard() {
  const measure = useMutation({ mutationFn: evaluateRecommendations })
  const result = measure.data

  return (
    <Card>
      <CardHeader
        title="Recommendation accuracy"
        description="Trains on older orders, then checks the suggestions against orders placed afterwards."
        action={
          <Button
            variant="secondary"
            size="sm"
            onClick={() => measure.mutate()}
            loading={measure.isPending}
          >
            {result ? 'Measure again' : 'Measure'}
          </Button>
        }
      />
      {measure.isError && (
        <CardBody>
          <p className="text-sm text-slate-500 dark:text-slate-400">
            Could not measure right now. Try again later.
          </p>
        </CardBody>
      )}
      {result && (
        <CardBody>
          <dl className="grid grid-cols-2 gap-4 sm:grid-cols-4">
            <Figure
              label={`Hit rate (top ${result.k})`}
              value={pct(result.hit_rate_at_k)}
              hint="Orders where at least one suggestion was bought"
            />
            <Figure
              label={`Precision (top ${result.k})`}
              value={pct(result.precision_at_k)}
              hint="Share of suggestions that were bought"
            />
            <Figure
              label="Recall"
              value={pct(result.recall_at_k)}
              hint="Share of the rest of the order we caught"
            />
            <Figure
              label="Coverage"
              value={pct(result.coverage)}
              hint="Orders we had any suggestion for"
            />
          </dl>
          <p className="mt-3 text-xs text-slate-500 dark:text-slate-400">
            {result.baskets_evaluated} orders from {result.held_out_from} to {result.held_out_to},
            trained on {result.trained_on} earlier orders.
          </p>
        </CardBody>
      )}
    </Card>
  )
}

function Figure({ label, value, hint }: { label: string; value: string; hint: string }) {
  return (
    <div>
      <dt className="text-xs text-slate-500 dark:text-slate-400">{label}</dt>
      <dd className="mt-1 text-xl font-semibold tabular-nums text-slate-900 dark:text-slate-100">
        {value}
      </dd>
      <dd className="text-xs text-slate-500 dark:text-slate-400">{hint}</dd>
    </div>
  )
}
